import { agentsApi } from '../../agents-api.ts';
import type { AgentInfo } from '../../agents-api.ts';
import { busy, button, linkButton, toast } from '../../components.ts';
import { icon } from '../../icons.ts';
import { h, markdown, when } from '../../ui.ts';

/** The agent page's side column: the native preview, the last summary, and the facts. */

const PREVIEW_TEXT: Record<AgentInfo['preview']['state'], string> = {
  none: 'No preview yet. It starts when the agent has something to show.',
  starting: 'Starting the preview…',
  ready: 'Running on its own branch.',
  failed: 'The preview did not start.',
};

const STATE_LABEL: Record<AgentInfo['preview']['state'], string> = {
  none: 'Not started',
  starting: 'Starting',
  ready: 'Ready',
  failed: 'Failed',
};

export function previewCard(a: AgentInfo, draw: (a: AgentInfo) => void): HTMLElement {
  const p = a.preview;
  const restart = button([icon.refresh(), p.state === 'none' ? 'Start preview' : 'Restart preview'], () => void go(), {
    variant: 'outline',
    size: 'sm',
  });
  const go = () =>
    busy(restart, async () => {
      draw(await agentsApi.preview(a.id));
      toast('Preview restarting');
    });
  const tone = p.state === 'ready' ? 'success' : p.state === 'failed' ? 'danger' : '';
  return h(
    'div',
    { class: `card preview-card ${p.state === 'ready' ? 'success' : ''}` },
    h(
      'div',
      { class: 'card-head' },
      h('span', { class: 'label' }, 'Preview'),
      h(
        'span',
        { class: `chip ${tone}` },
        p.state === 'starting' ? h('span', { class: 'dot pulse' }) : null,
        STATE_LABEL[p.state],
      ),
    ),
    p.url ? h('div', { class: 'mono truncate url' }, p.url) : null,
    h('p', { class: 'muted small' }, p.state === 'failed' && p.error ? p.error : PREVIEW_TEXT[p.state]),
    h(
      'div',
      { class: 'row' },
      p.state === 'ready' && p.url ? linkButton([icon.open(), 'Open preview'], p.url, 'default', 'sm') : null,
      restart,
    ),
  );
}

export function summaryCard(a: AgentInfo): HTMLElement | null {
  if (!a.summary) return null;
  const md = markdown(a.summary);
  md.classList.add('bare');
  return h('div', { class: 'card' }, h('span', { class: 'label' }, 'Summary'), md);
}

export function detailsCard(a: AgentInfo): HTMLElement {
  const row = (k: string, v: Node | string) => h('div', { class: 'kv' }, h('dt', {}, k), h('dd', {}, v));
  return h(
    'div',
    { class: 'card' },
    h('span', { class: 'label' }, 'Task'),
    h('p', { class: 'task-text' }, a.task),
    h(
      'dl',
      { class: 'kvs' },
      row('Project', h('a', { class: 'link', href: `#/projects/${encodeURIComponent(a.project)}` }, a.project)),
      row('Branch', h('span', { class: 'mono' }, a.branch)),
      row('Started', when(a.createdAt)),
      row('Updated', when(a.updatedAt)),
      a.talkSessionId
        ? row('From talk', h('a', { class: 'link', href: `#/session/${a.talkSessionId}` }, 'open talk'))
        : null,
      a.sessionId ? row('Session', h('span', { class: 'mono truncate' }, a.sessionId)) : null,
    ),
  );
}
